/**
 * Project selector with server selection and search functionality
 */

import { useState, useEffect, type ReactElement } from 'react';
import { ROUTES } from '@utils/constants';
import { ServerSelect } from '../../git/components/ServerSelect';
import { useGitProjects } from '../../git/hooks/useGitProjects';
import { useGitCredentials } from '../../git/hooks/useGitCredentials';
import { useConnectGitServer } from '../../git/hooks/useConnectGitServer';
import type { GitProject } from '../../git/types/git.types';

interface ProjectSelectorProps {
  selectedProject: GitProject | null;
  onSelect: (project: GitProject | null) => void;
  disabled?: boolean;
  /** Project ids to hide from the dropdown (e.g. projects already linked to a pack). */
  excludeProjectIds?: readonly string[];
}

export function ProjectSelector({
  selectedProject,
  onSelect,
  disabled = false,
  excludeProjectIds,
}: ProjectSelectorProps): ReactElement {
  const [serverId, setServerId] = useState<string | null>(
    selectedProject?.server_id ?? null
  );
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => { clearTimeout(timer); };
  }, [search]);

  const { data: credsData } = useGitCredentials();
  const { connect, isConnecting, error: connectError } = useConnectGitServer();
  const cred = credsData?.credentials.find((c) => c.server_id === serverId);
  const needsConnect = cred?.auth_method === 'oauth' && !cred.login_connected;
  const needsToken = cred?.auth_method === 'none';

  // Skip the fetch entirely when we already know from the credential state
  // it can't succeed — an empty repository list would otherwise look like
  // "you have no repositories" instead of "you are not connected".
  const { data, isLoading, error } = useGitProjects(
    needsConnect || needsToken ? null : serverId,
    debouncedSearch,
    1,
    20,
  );

  const excluded = new Set(excludeProjectIds ?? []);
  const fetched = data?.projects ?? [];
  const projects = fetched.filter((p) => !excluded.has(p.id));
  const canList = !needsConnect && !needsToken && !isLoading && !error && data !== undefined;

  const handleServerChange = (id: string): void => {
    setServerId(id);
    onSelect(null);
    setSearch('');
    setDebouncedSearch('');
  };

  const handleSelect = (project: GitProject): void => {
    onSelect(project);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="form-select">
      <div className="form-select__compound">
        <ServerSelect
          value={serverId}
          onChange={handleServerChange}
          requireCredential
          compact
        />
        <span className="form-select__divider" aria-hidden="true" />
        <button
          type="button"
          onClick={() => { setIsOpen(!isOpen); }}
          disabled={disabled || !serverId}
          className="form-select__trigger form-select__trigger--bare"
        >
          {selectedProject ? (
            <span className="form-select__value">{selectedProject.path}</span>
          ) : (
            <span className="form-select__placeholder">Select project...</span>
          )}
          <span className="form-select__chevron">{isOpen ? '▲' : '▼'}</span>
        </button>
      </div>

      {isOpen && serverId && (
        <div className="form-select__dropdown">
          <div className="form-select__search">
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); }}
              placeholder="Search projects..."
              className="form-select__search-input"
              autoFocus
            />
          </div>

          {needsConnect && (
            <div className="form-select__message">
              Connect to {cred.server_display_name} to see your repositories.
              <button
                type="button"
                className="secondary-button"
                onClick={() => { void connect(cred.login_provider_name ?? ''); }}
                disabled={isConnecting || !cred.login_provider_name}
              >
                {isConnecting ? 'Connecting…' : 'Connect'}
              </button>
              {connectError && <div className="form-select__error">{connectError}</div>}
            </div>
          )}

          {needsToken && (
            <div className="form-select__message">
              No credential for {cred.server_display_name}.{' '}
              <a href={ROUTES.SETTINGS}>Add a token in Settings</a>.
            </div>
          )}

          {!needsConnect && !needsToken && isLoading && (
            <div className="form-select__message">Loading...</div>
          )}

          {!needsConnect && !needsToken && error && (
            <div className="form-select__message form-select__error">
              {error.message}
            </div>
          )}

          {canList && fetched.length === 0 && (
            <div className="form-select__message">No projects found</div>
          )}

          {canList && fetched.length > 0 && projects.length === 0 && (
            <div className="form-select__message">All matching projects are already linked</div>
          )}

          {!needsConnect && !needsToken && projects.map((project) => (
            <button
              key={project.id}
              type="button"
              onClick={() => { handleSelect(project); }}
              className="form-select__option"
            >
              <div className="form-select__option-text">
                <span>{project.name}</span>
                <span className="form-select__option-sub">
                  {project.path}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
